import { merge } from 'lodash';
import { GetRosterInfo_vm_activity as RosterType } from './__generated__/GetRosterInfo';

export type SessionRostering = {
  session: {
    sessionId: string;
  };
};

export type ParsedSessions = {
  [key: string]: boolean;
};

export type ParsedRoster = {
  initialDraftRoster: string[];
  draftRoster: string[];
  publishedRoster: string[];
  activityUnavailabilityId?: string;
  availabilities: string[];
  volunteerId: string;
  enrolmentId?: string;
  enrolled: boolean;
  isConflicting: boolean;
  hasLeaveConflict: boolean;
  profile: {
    __typename: 'VOLUNTEER_ProfileType';
    profileId: string;
    preferredName: string;
    lastName: string;
    avatarUrl: string | null;
    userId: string;
  };
  publishedSessionIds: string[];
  draftedSessionIds: string[];
  draftedSessionTimes?: { [sessionId: string]: { startTime: string | null; endTime: string | null } };
  initialDraftedSessionTimes?: { [sessionId: string]: { startTime: string | null; endTime: string | null } };
  unavailable: boolean;
  attending: boolean | null;
  availability: {
    sessionAvailabilities?: {
      session: { sessionId: string };
    }[];
  } | null;
};

export type RosterByDateType = {
  volunteers: { [volunteerId: string]: ParsedRoster };
  isPublished: boolean;
  cancelledActivityId?: string;
  activityDate: string;
  rosterNotes?: string | null;
  publishedRoster: any;
};

export const keyToBooleanObject = (keys: string[]) => keys.reduce((keyMap, key) => ({ ...keyMap, [key]: true }), {});
export const mergeAllObject = (objects: any[]) =>
  objects.reduce((objectMap, object) => ({ ...objectMap, ...object }), {});

// Check if an activity date conflicts with volunteer unavailability periods
export const hasLeaveConflict = (activityDate: string, unavailabilities: Array<{startDate: string, endDate: string}>) => {
  if (!unavailabilities || unavailabilities.length === 0) return false;
  
  const activityDateObj = new Date(activityDate);
  
  return unavailabilities.some(period => {
    const startDate = new Date(period.startDate);
    const endDate = new Date(period.endDate);
    return activityDateObj >= startDate && activityDateObj <= endDate;
  });
};

function parseSessionTimes(
  sessionRosterings: Array<{ session: { sessionId: string }; startTime?: string | null; endTime?: string | null }>
): { [sessionId: string]: { startTime: string | null; endTime: string | null } } | undefined {
  const times: { [sessionId: string]: { startTime: string | null; endTime: string | null } } = {};
  let hasAnyTimes = false;
  sessionRosterings.forEach((sr) => {
    if (sr.startTime || sr.endTime) {
      hasAnyTimes = true;
      times[sr.session.sessionId] = { startTime: sr.startTime ?? null, endTime: sr.endTime ?? null };
    }
  });
  return hasAnyTimes ? times : undefined;
}

export function parseRosterByVolunteerId(
  roster?: RosterType | null,
  activityDate?: string
): {
  [key: string]: ParsedRoster;
} {
  if (!roster) {
    return {};
  }

  const { draftRoster, publishedRoster, activityEnrolments: enrolments } = roster;

  const publishedRosterByVolunteerIds = mergeAllObject(
    (publishedRoster?.activeRosterings || []).map((rostering) => {
      const matchedEnrolment = enrolments.find((e) => e.volunteer.volunteerId === rostering.volunteer.volunteerId);
      const matchedDraftRostering = draftRoster?.draftRosterings.find(
        (dr) => dr.volunteer.volunteerId === rostering.volunteer.volunteerId
      );

      // Check for leave conflicts using the matched enrolment which has unavailabilities
      const volunteerHasLeaveConflict = activityDate && matchedEnrolment?.volunteer.unavailabilities 
        ? hasLeaveConflict(activityDate, matchedEnrolment.volunteer.unavailabilities)
        : false;

      return {
        [rostering.volunteer.volunteerId]: {
          ...rostering.volunteer,
          publishedRoster: rostering.sessionRosterings
            .map((sessionRostering) => sessionRostering.session.sessionId)
            .sort(),
          attending: rostering.attending,
          enrolled: !!matchedEnrolment,
          publishedSessionIds: rostering.sessionRosterings.map((sr) => sr.session.sessionId),
          draftedSessionIds: matchedDraftRostering
            ? matchedDraftRostering.sessionRosterings.map((sr) => sr.session.sessionId)
            : [],
          draftedSessionTimes: matchedDraftRostering
            ? parseSessionTimes(matchedDraftRostering.sessionRosterings)
            : parseSessionTimes(rostering.sessionRosterings),
          initialDraftedSessionTimes: matchedDraftRostering
            ? parseSessionTimes(matchedDraftRostering.sessionRosterings)
            : parseSessionTimes(rostering.sessionRosterings),
          unavailable: !!matchedEnrolment?.unavailability ?? false,
          availability: matchedEnrolment?.availability ?? null,
          isConflicting: false,
          hasLeaveConflict: volunteerHasLeaveConflict
        },
      };
    })
  );

  const draftRosterByVolunteerIds = mergeAllObject(
    (draftRoster?.draftRosterings || []).map((rostering) => {
      const matchedEnrolment = enrolments.find((e) => e.volunteer.volunteerId === rostering.volunteer.volunteerId);

      // Check for leave conflicts using the matched enrolment which has unavailabilities
      const volunteerHasLeaveConflict = activityDate && matchedEnrolment?.volunteer.unavailabilities 
        ? hasLeaveConflict(activityDate, matchedEnrolment.volunteer.unavailabilities)
        : false;

      return {
        [rostering.volunteer.volunteerId]: {
          ...rostering.volunteer,
          initialDraftRoster: rostering.sessionRosterings
            .map((sessionRostering) => sessionRostering.session.sessionId)
            .sort(),
          draftRoster: rostering.sessionRosterings.map((sessionRostering) => sessionRostering.session.sessionId).sort(),
          enrolled: !!matchedEnrolment,
          publishedSessionIds: [],
          draftedSessionIds: rostering.sessionRosterings.map((sr) => sr.session.sessionId),
          draftedSessionTimes: parseSessionTimes(rostering.sessionRosterings),
          initialDraftedSessionTimes: parseSessionTimes(rostering.sessionRosterings),
          unavailable: !!matchedEnrolment?.unavailability ?? false,
          availability: matchedEnrolment?.availability ?? null,
          attending: null,
          isConflicting: false,
          hasLeaveConflict: volunteerHasLeaveConflict
        },
      };
    })
  );

  const enrolmentByVolunteerIds = mergeAllObject(
    (enrolments || []).map((enrolment) => {
      // Check for leave conflicts if activityDate is provided
      const volunteerHasLeaveConflict = activityDate && enrolment.volunteer.unavailabilities 
        ? hasLeaveConflict(activityDate, enrolment.volunteer.unavailabilities)
        : false;

      // Mark as unavailable if they have regular unavailability OR if they're on leave
      const isUnavailable = Boolean(enrolment.unavailability && !enrolment.unavailability.dateRemoved) || volunteerHasLeaveConflict;
      
      return {
        [enrolment.volunteer.volunteerId]: {
          ...enrolment.volunteer,
          enrolmentId: enrolment.activityEnrolmentId,
          unavailable: isUnavailable,
          activityUnavailabilityId: enrolment.unavailability?.activityUnavailabilityId,
          availabilities: (enrolment.availability?.sessionAvailabilities || [])
            .filter((sessionAvailability) => !sessionAvailability.dateRemoved)
            .map((availability) => availability.session.sessionId),
          enrolled: true,
          publishedSessionIds: [],
          draftedSessionIds: [],
          availability: enrolment.availability,
          attending: null,
          activityEnrolmentId: enrolment.activityEnrolmentId,
          isConflicting: false,
          hasLeaveConflict: volunteerHasLeaveConflict
        },
      };
    })
  );

  return merge(publishedRosterByVolunteerIds, draftRosterByVolunteerIds, enrolmentByVolunteerIds);
}
